import * as THREE from 'three';

const loader = new THREE.TextureLoader();

function loadImage(url) {
  return new Promise((resolve) => {
    loader.load(
      url,
      (tex) => resolve(tex),
      undefined,
      () => resolve(null),
    );
  });
}

function canvasTexture(draw, size = 1024) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  draw(ctx, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  return tex;
}

function hash(x, y) {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

function fbm(x, y) {
  let v = 0;
  let a = 0.5;
  let f = 1;
  for (let i = 0; i < 5; i++) {
    v += a * hash(x * f, y * f);
    f *= 2.02;
    a *= 0.5;
  }
  return v;
}

export function makeWoodFallback() {
  return canvasTexture((ctx, s) => {
    const img = ctx.createImageData(s, s);
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const u = x / s;
        const v = y / s;
        const grain = Math.sin((v + fbm(u * 3, v * 18) * 0.08) * Math.PI * 42);
        const wave = fbm(u * 2.2, v * 9);
        const tone = 0.52 + grain * 0.08 + wave * 0.12;
        const r = 140 + tone * 70;
        const g = 78 + tone * 48;
        const b = 42 + tone * 22;
        const i = (y * s + x) * 4;
        img.data[i] = r;
        img.data[i + 1] = g;
        img.data[i + 2] = b;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
}

export function makeSkinFallback() {
  return canvasTexture((ctx, s) => {
    ctx.fillStyle = '#e6d4ad';
    ctx.fillRect(0, 0, s, s);
    const img = ctx.getImageData(0, 0, s, s);
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const n = fbm(x * 0.01, y * 0.01);
        const pore = hash(x * 0.7, y * 0.7);
        const i = (y * s + x) * 4;
        const shade = (n - 0.5) * 28;
        img.data[i] = 228 + shade - (pore > 0.97 ? 18 : 0);
        img.data[i + 1] = 208 + shade * 0.9 - (pore > 0.97 ? 16 : 0);
        img.data[i + 2] = 168 + shade * 0.7 - (pore > 0.97 ? 14 : 0);
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  });
}

export function makeLeatherFallback() {
  return canvasTexture((ctx, s) => {
    const img = ctx.createImageData(s, s);
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const n = fbm(x * 0.012, y * 0.012);
        const c = 48 + n * 42;
        const i = (y * s + x) * 4;
        img.data[i] = c + 18;
        img.data[i + 1] = c * 0.62;
        img.data[i + 2] = c * 0.4;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, 512);
}

export function makeBoneTex() {
  return canvasTexture((ctx, s) => {
    const img = ctx.createImageData(s, s);
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const g = fbm(x * 0.02, y * 0.035);
        const hair = Math.sin((x / s) * Math.PI * 80 + g * 4) * 6;
        const i = (y * s + x) * 4;
        img.data[i] = 232 + g * 14 + hair;
        img.data[i + 1] = 220 + g * 12 + hair * 0.7;
        img.data[i + 2] = 196 + g * 10;
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, 512);
}

export function makePegWoodTex(wood) {
  const src = wood.image;
  const s = 512;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d');
  if (src) ctx.drawImage(src, 0, 0, s, s);
  else {
    ctx.fillStyle = '#4a2a18';
    ctx.fillRect(0, 0, s, s);
  }
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = 'rgba(42, 20, 12, 0.72)';
  ctx.fillRect(0, 0, s, s);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 8;
  return tex;
}

export function roughnessFrom(tex, contrast = 1.1, invert = false) {
  const img = tex.image;
  const w = img.width || 512;
  const h = img.height || 512;
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, 512, 512);
  const data = ctx.getImageData(0, 0, 512, 512);
  for (let i = 0; i < data.data.length; i += 4) {
    let l = (data.data[i] * 0.3 + data.data[i + 1] * 0.59 + data.data[i + 2] * 0.11) / 255;
    l = Math.pow(l, contrast);
    if (invert) l = 1 - l;
    const v = Math.floor(THREE.MathUtils.clamp(l, 0, 1) * 255);
    data.data[i] = data.data[i + 1] = data.data[i + 2] = v;
  }
  ctx.putImageData(data, 0, 0);
  const out = new THREE.CanvasTexture(canvas);
  out.wrapS = out.wrapT = THREE.RepeatWrapping;
  out.anisotropy = 8;
  return out;
}

export function bumpFrom(tex) {
  return roughnessFrom(tex, 0.85, false);
}

function prep(tex, repeatX = 1, repeatY = 1) {
  if (!tex) return null;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeatX, repeatY);
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

export async function loadStudioTextures() {
  const [woodImg, skinImg, leatherImg] = await Promise.all([
    loadImage('/textures/wood.jpg'),
    loadImage('/textures/skin.jpg'),
    loadImage('/textures/leather.jpg'),
  ]);

  const wood = prep(woodImg, 1.6, 3.2) || makeWoodFallback();
  wood.repeat.set(1.6, 3.2);
  const skin = prep(skinImg, 1.1, 1.3) || makeSkinFallback();
  const leather = prep(leatherImg, 1.4, 0.45) || makeLeatherFallback();
  const bone = makeBoneTex();
  const pegWood = makePegWoodTex(wood);
  pegWood.repeat.set(1.2, 1.2);

  return {
    wood,
    woodRough: roughnessFrom(wood, 1.25, true),
    woodBump: bumpFrom(wood),
    skin,
    skinRough: roughnessFrom(skin, 0.9, true),
    skinBump: bumpFrom(skin),
    leather,
    leatherRough: roughnessFrom(leather, 1.1, true),
    bone,
    boneRough: roughnessFrom(bone, 1.05, true),
    pegWood,
    pegRough: roughnessFrom(pegWood, 1.2, true),
  };
}
